import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { useRecoilValue } from 'recoil';

import {
  giftCouponSearchDataAtomFamily,
  giftCouponSearchStatusAtomFamily,
} from '@shared/api/giftCoupon/giftCoupon.atom';
import {
  UpdateDeliveredCouponFieldsDto,
  updateDeliveredCouponFields,
} from '@shared/api/giftCoupon/giftCoupon.controller';
import { useGiftCouponSearchHook } from '@shared/api/giftCoupon/giftCoupon.hook';
import {
  GiftCouponRedeemStatus,
  GiftCouponSearchParams,
} from '@shared/api/giftCoupon/giftCoupon.interface';
import { useQueryClient } from 'react-query';

import { executeUndeliver, formatUndeliverToast } from '../utils/undeliver.utils';
import { useSessionPersistedState } from './useSessionPersistedState';
import { useToast } from './useToast';

/** KST 기준 오늘 날짜 (YYYY-MM-DD) */
export const todayKSTString = () =>
  new Date(Date.now() + 9 * 60 * 60 * 1000).toISOString().slice(0, 10);

const DATE_PATTERN = /^\d{4}-\d{2}-\d{2}$/;

/**
 * 번호 검색 최소 입력 길이 — 이 검색은 날짜 범위 없이 전체 기간을 훑기 때문에
 * 1~3자로는 사실상 전수 스캔이 된다. (전화번호 뒷 4자리 기준)
 */
export const MIN_SEARCH_LENGTH = 4;

/**
 * 발송내역 조회 페이지 훅.
 *
 * 별도 백엔드 API 없이 기존 /admin/gift-coupon/search 를 사용한다 —
 * redeemStatus=Delivered + gifticon.deliveredAt KST 하루 범위(dateField='deliveredAt')로
 * 조회하면 발송대기 대시보드의 todayDelivered와 동일한 populate/shape이 내려온다.
 * 도매가/매체 일괄수정도 날짜와 무관한 generic PATCH(updateDeliveredCouponFields)를 재사용.
 *
 * 번호 검색 모드에서는 날짜 필터를 빼고 anyNumber(수신자번호/확인번호/쿠폰번호 OR)로만
 * 조회한다 — 발송일을 모르는 CS 문의용. 조회 결과 shape은 동일해 같은 표를 그대로 쓴다.
 */
export function useDeliveredHistoryPage() {
  const queryClient = useQueryClient();

  // 새로고침 후에도 보던 날짜 유지 (환불/수정 후 reload 대비)
  const [date, setDate] = useSessionPersistedState(
    'giftDeliveredHistory.date',
    todayKSTString(),
  );

  // 번호 검색 — 발송 날짜를 모를 때 전체 기간에서 찾는다.
  // 실행된 검색어만 저장(새로고침 복원용), 입력 중인 값은 일반 state.
  const [searchQuery, setSearchQuery] = useSessionPersistedState(
    'giftDeliveredHistory.searchQuery',
    '',
  );
  const [searchInput, setSearchInput] = useState(searchQuery);
  const trimmedQuery = searchQuery.trim();
  const isSearchMode = trimmedQuery.length >= MIN_SEARCH_LENGTH;

  // 잔액 변경내역 모달 등에서 ?date=YYYY-MM-DD로 진입하면 그 날짜를 연다 (진입 시 1회).
  // 세션에 남아있던 검색어는 지워야 날짜 조회 모드로 열린다.
  const appliedUrlDateRef = useRef(false);
  useEffect(() => {
    if (appliedUrlDateRef.current) return;
    appliedUrlDateRef.current = true;
    const param = new URLSearchParams(window.location.search).get('date');
    if (param && DATE_PATTERN.test(param)) {
      setDate(param);
      setSearchQuery('');
      setSearchInput('');
    }
  }, [setDate, setSearchQuery]);
  const isValidDate = DATE_PATTERN.test(date);
  const isToday = date === todayKSTString();

  const searchParams = useMemo<GiftCouponSearchParams>(
    () =>
      isSearchMode
        ? {
            redeemStatus: [GiftCouponRedeemStatus.Delivered],
            anyNumber: trimmedQuery,
          }
        : {
            redeemStatus: [GiftCouponRedeemStatus.Delivered],
            dateFrom: date,
            dateTo: date,
            dateField: 'deliveredAt',
          },
    [isSearchMode, trimmedQuery, date],
  );
  const searchKey = isSearchMode
    ? `deliveredHistory:q:${trimmedQuery}`
    : `deliveredHistory:${date}`;

  useGiftCouponSearchHook(searchKey, searchParams, !isSearchMode && !isValidDate);

  const status = useRecoilValue(giftCouponSearchStatusAtomFamily(searchKey));
  const rawResults = useRecoilValue(giftCouponSearchDataAtomFamily(searchKey));

  // 발송시각 내림차순 → 같은 묶음 내 신청시각 오름차순 (발송대기 페이지의 오늘발송완료와 동일 정렬)
  const delivered = useMemo(() => {
    const list = rawResults ?? [];
    return [...list].sort((a, b) => {
      const deliveredA = a.gifticon?.deliveredAt
        ? new Date(a.gifticon.deliveredAt).getTime()
        : 0;
      const deliveredB = b.gifticon?.deliveredAt
        ? new Date(b.gifticon.deliveredAt).getTime()
        : 0;
      if (deliveredA !== deliveredB) return deliveredB - deliveredA;
      const requestedA = a.recipient?.requestedAt
        ? new Date(a.recipient.requestedAt).getTime()
        : 0;
      const requestedB = b.recipient?.requestedAt
        ? new Date(b.recipient.requestedAt).getTime()
        : 0;
      return requestedA - requestedB;
    });
  }, [rawResults]);

  // 날짜 조작은 곧 "날짜로 보겠다"는 뜻이라 검색 모드에서 자동으로 빠져나온다.
  const clearSearch = useCallback(() => {
    setSearchInput('');
    setSearchQuery('');
  }, [setSearchQuery]);

  const selectDate = useCallback(
    (value: string) => {
      setDate(value);
      clearSearch();
    },
    [setDate, clearSearch],
  );

  // 날짜 이동 (UTC 자정 기준 문자열 연산이라 타임존 영향 없음)
  const shiftDate = useCallback(
    (days: number) => {
      if (!isValidDate) return;
      const d = new Date(`${date}T00:00:00Z`);
      d.setUTCDate(d.getUTCDate() + days);
      selectDate(d.toISOString().slice(0, 10));
    },
    [date, isValidDate, selectDate],
  );

  const goToday = useCallback(() => selectDate(todayKSTString()), [selectDate]);

  const submitSearch = useCallback(() => {
    const value = searchInput.trim();
    if (value.length > 0 && value.length < MIN_SEARCH_LENGTH) {
      alert(`번호는 ${MIN_SEARCH_LENGTH}자 이상 입력해주세요. (전화번호 뒷자리 등)`);
      return;
    }
    setSearchQuery(value);
  }, [searchInput, setSearchQuery]);

  const [showTable, setShowTable] = useState(true);

  const {
    message: toastMessage,
    show: showToast,
    durationMs: toastDurationMs,
  } = useToast(1200);

  const copyToClipboard = useCallback(
    (text: string) => {
      navigator.clipboard.writeText(text);
      showToast('복사되었습니다.');
    },
    [showToast],
  );

  // 도매가/매체 일괄 수정 — 발송대기 페이지의 handleBulkUpdateDeliveredCoupon과 동일 로직
  const handleBulkUpdateDelivered = useCallback(
    async (couponIds: string[], dto: UpdateDeliveredCouponFieldsDto) => {
      if (couponIds.length === 0) return;
      if (dto.deliveryChannel === undefined && dto.priceWhole === undefined) return;

      const parts: string[] = [];
      if (dto.priceWhole !== undefined) {
        parts.push(`도매가: ${dto.priceWhole.toLocaleString()}원`);
      }
      if (dto.deliveryChannel !== undefined) {
        parts.push(`매체: ${dto.deliveryChannel || '입력안함'}`);
      }
      if (
        !confirm(`${couponIds.length}건을 다음과 같이 수정합니다.\n${parts.join('\n')}`)
      ) {
        return;
      }

      let successCount = 0;
      let failCount = 0;

      for (const id of couponIds) {
        try {
          await updateDeliveredCouponFields(id, dto);
          successCount++;
        } catch (error) {
          console.error(`쿠폰 수정 실패 (${id}):`, error);
          failCount++;
        }
      }

      // 이 페이지의 검색 결과 + 발송대기 대시보드(오늘 날짜 수정 시) 모두 갱신
      await queryClient.refetchQueries({
        predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
      });

      if (failCount === 0) {
        showToast(`${successCount}건 수정 완료`);
      } else {
        alert(`성공: ${successCount}건, 실패: ${failCount}건`);
      }
    },
    [queryClient, showToast],
  );

  // 발송완료 취소 — 발송대기(또는 발송보류)로 복귀 + 원장 자동 환급
  // (스펙: docs/gift-coupon-undeliver-spec.md · docs/gift-coupon-onhold-spec.md)
  const handleBulkUndeliver = useCallback(
    async (couponIds: string[], reason: string, hold = false) => {
      const result = await executeUndeliver(couponIds, reason, hold);
      if (!result) return false;
      // 이 페이지 검색 결과 + 발송대기 대시보드(Requested 복귀분) 모두 갱신
      await queryClient.refetchQueries({
        predicate: (query) => String(query.queryKey).includes('/gift-coupon/'),
      });
      showToast(formatUndeliverToast(result));
      return true;
    },
    [queryClient, showToast],
  );

  return {
    // 날짜
    date,
    selectDate,
    isToday,
    isValidDate,
    shiftDate,
    goToday,

    // 번호 검색 (검색 모드에서는 날짜 무시 · 전체 기간)
    searchInput,
    setSearchInput,
    submitSearch,
    clearSearch,
    isSearchMode,
    searchQuery: trimmedQuery,

    // 데이터
    status,
    delivered,

    // 테이블 접기/펼치기
    showTable,
    setShowTable,

    // 일괄 수정 / 발송취소
    handleBulkUpdateDelivered,
    handleBulkUndeliver,

    // 토스트/클립보드
    toastMessage,
    showToast,
    toastDurationMs,
    copyToClipboard,
  };
}
